import {isNil} from 'ramda';

import type {Nullish} from 'shared/web';

import {calculateReportShares} from './calculateReportShares';

/** A category's contribution to its row total, including rows whose total is negative. */
export function calculateReportCompositionPercentages(
  amounts: Array<number | Nullish>,
  total: number | Nullish
) {
  const unavailable = {parts: amounts.map(() => null), total: null};
  const sum = amounts.reduce<number>((result, amount) => result + (amount ?? Number.NaN), 0);
  // Cancellation amplifies addition errors relative to a small nonzero total.
  const magnitude = amounts.reduce<number>(
    (result, amount) => result + Math.abs(amount ?? Number.NaN),
    0
  );
  const tolerance = Math.max(
    Math.abs(total ?? 0) * 1e-10,
    Number.EPSILON * amounts.length * magnitude
  );
  // An incomplete partition cannot truthfully show shares adding up to the displayed total.
  if (
    isNil(total) ||
    !Number.isFinite(total) ||
    total === 0 ||
    !Number.isFinite(sum) ||
    sum === 0 ||
    Math.abs(sum - total) > tolerance
  ) {
    return unavailable;
  }
  const sign = total < 0 ? -1 : 1;
  const parts = calculateReportShares(
    amounts.map((amount) => (isNil(amount) ? amount : amount * sign)),
    total * sign
  );
  return parts.some((part) => isNil(part) || !Number.isFinite(part))
    ? unavailable
    : {parts, total: 100};
}
